import React from "react";
import { MapPin, Phone, ExternalLink, Store } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Retail partner section — surfaces physical stores where 4keeperz gloves
 * can be bought locally. Boosts local SEO via LocalBusiness cross-references.
 */
const RETAILERS = [
  {
    slug: "ks-sport",
    name: "KS-Sport AG",
    logo:
      "https://www.ks-sport.ch/bilder/intern/shoplogo/ks-sport%20Web-Logo%20150502.jpg",
    tagline_de: "Fachhandel für Fussball & Leichtathletik",
    tagline_en: "Football & athletics specialist retailer",
    street: "Wägitalstrasse 22",
    city: "8854 Siebnen",
    region: "SZ",
    country: "Schweiz",
    phone: "+41 55 440 21 20",
    web: "https://www.ks-sport.ch/",
    shop_de: "Auch im Onlineshop erhältlich",
    shop_en: "Also in online shop",
    products_de: "Mamba Edition · Panther Edition",
    products_en: "Mamba Edition · Panther Edition",
    maps: "https://www.google.com/maps/search/?api=1&query=KS-Sport+AG+W%C3%A4gitalstrasse+22+8854+Siebnen",
  },
];

const StockistsSection = () => {
  const { lang } = useLanguage();
  const isDe = lang === "de";

  const t = {
    kicker: isDe ? "Vor Ort · Retail-Partner" : "In store · Retail partners",
    title: isDe ? "Auch offline erhältlich." : "Available offline too.",
    subtitle: isDe
      ? "Handschuhe anfassen, testen, direkt mitnehmen — bei unseren zertifizierten Fachhandels-Partnern."
      : "Feel the gloves, try them on, take them home — at our certified specialist retail partners.",
    visit: isDe ? "Online besuchen" : "Visit online",
    directions: isDe ? "Route anzeigen" : "Get directions",
    call: isDe ? "Anrufen" : "Call",
  };

  return (
    <section
      id="stockists"
      data-testid="stockists-section"
      className="relative py-20 md:py-24 bg-[#0A0A0A] border-b border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#00A651]/10 border border-[#00A651]/40 text-[#00A651] font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 mb-4">
              <Store className="w-3 h-3" />
              {t.kicker}
            </div>
            <h2 className="font-brush text-5xl md:text-6xl leading-none text-white">
              {t.title}
            </h2>
            <p className="text-white/60 mt-3 max-w-xl">{t.subtitle}</p>
          </div>
        </div>

        {/* Retailer cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {RETAILERS.map((r) => (
            <article
              key={r.slug}
              data-testid={`stockist-${r.slug}`}
              itemScope
              itemType="https://schema.org/SportingGoodsStore"
              className="border border-white/10 bg-black hover:border-[#00A651]/40 transition-colors overflow-hidden"
            >
              <meta itemProp="name" content={r.name} />
              <meta itemProp="telephone" content={r.phone} />
              <meta itemProp="url" content={r.web} />
              <div className="p-6 md:p-7 flex flex-col gap-5">
                {/* Logo + name */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white p-2 border border-white/10 shrink-0 flex items-center justify-center">
                    <img
                      src={r.logo}
                      alt={r.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-brush text-3xl text-white leading-tight">
                      {r.name}
                    </div>
                    <div className="text-[11px] text-white/50 font-condensed uppercase tracking-widest mt-0.5">
                      {isDe ? r.tagline_de : r.tagline_en}
                    </div>
                  </div>
                </div>

                {/* Products */}
                <div className="border-l-2 border-[#00A651] pl-3 py-1">
                  <div className="text-[10px] text-[#00A651] font-condensed uppercase tracking-widest">
                    {isDe ? "Verfügbar" : "In stock"}
                  </div>
                  <div className="text-white/90 text-sm mt-0.5">
                    {isDe ? r.products_de : r.products_en}
                  </div>
                </div>

                {/* Address */}
                <div
                  itemProp="address"
                  itemScope
                  itemType="https://schema.org/PostalAddress"
                  className="text-sm text-white/70 flex items-start gap-2"
                >
                  <MapPin className="w-4 h-4 mt-0.5 text-[#00A651] shrink-0" />
                  <div>
                    <div itemProp="streetAddress">{r.street}</div>
                    <div>
                      <span itemProp="postalCode">
                        {r.city.split(" ")[0]}
                      </span>{" "}
                      <span itemProp="addressLocality">
                        {r.city.split(" ").slice(1).join(" ")}
                      </span>
                      , <span itemProp="addressRegion">{r.region}</span>
                      <meta itemProp="addressCountry" content="CH" />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-1">
                  <a
                    href={r.web}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`stockist-${r.slug}-visit`}
                    className="inline-flex items-center gap-1.5 border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black font-condensed uppercase tracking-widest text-[11px] px-3 py-2 transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t.visit}
                  </a>
                  <a
                    href={r.maps}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`stockist-${r.slug}-directions`}
                    className="inline-flex items-center gap-1.5 border border-white/20 text-white/80 hover:border-white hover:text-white font-condensed uppercase tracking-widest text-[11px] px-3 py-2 transition-colors"
                  >
                    <MapPin className="w-3 h-3" />
                    {t.directions}
                  </a>
                  <a
                    href={`tel:${r.phone.replace(/\s/g, "")}`}
                    data-testid={`stockist-${r.slug}-call`}
                    className="inline-flex items-center gap-1.5 border border-white/20 text-white/80 hover:border-white hover:text-white font-condensed uppercase tracking-widest text-[11px] px-3 py-2 transition-colors"
                  >
                    <Phone className="w-3 h-3" />
                    {t.call}
                  </a>
                </div>
              </div>
            </article>
          ))}

          {/* Slot for future partners — invitation card */}
          <article
            data-testid="stockist-partner-invite"
            className="border border-dashed border-white/15 bg-black/40 p-6 md:p-7 flex flex-col justify-center items-center text-center gap-3 min-h-[280px]"
          >
            <div className="w-12 h-12 border border-white/15 flex items-center justify-center">
              <Store className="w-5 h-5 text-white/40" />
            </div>
            <div className="font-brush text-2xl text-white/80">
              {isDe ? "Dein Shop hier?" : "Your shop here?"}
            </div>
            <p className="text-white/50 text-sm max-w-xs">
              {isDe
                ? "Wir suchen Fachhandels-Partner in Deutschland, Österreich und der übrigen Schweiz."
                : "We're looking for specialist retail partners in Germany, Austria and the rest of Switzerland."}
            </p>
            <a
              href="mailto:info@4keeperz.ch?subject=Retail%20Partnership"
              className="mt-1 inline-flex items-center gap-1.5 border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black font-condensed uppercase tracking-widest text-[11px] px-3 py-2 transition-colors"
            >
              {isDe ? "Kontakt aufnehmen" : "Get in touch"}
              <ExternalLink className="w-3 h-3" />
            </a>
          </article>
        </div>
      </div>
    </section>
  );
};

export default StockistsSection;
